import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '@mercator-gcp/grout';
import { Document } from '@mercator-gcp/types/elastic_search_service/documents/DocumentDetailFilterResponse';
import { EmptyField } from 'components/elements//EmptyField';
import { FlexRow } from 'components/layout/FlexRow';
import { MultipleProjectLinksModal } from 'components/widgets/DocumentSearch/MultipleProjectLinksModal';
import { trackProjectListClick } from 'utils/analytics';
import { ProjectGroupTypes } from 'utils/analytics/constants';
import { DOCUMENT_TYPES } from 'utils/constants/documentTypes';
import { ddpUrl, pdpUrl } from 'utils/navigation';

const containerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
};

const emptyFieldStyle = {
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
};

const VIEW_DOCUMENT_BTN_LABEL = 'View Project';
const VIEW_PROJECT_SINGLE_BTN_LABEL = 'View Project';
const VIEW_PROJECT_MULTI_BTN_LABEL = (count?: number) => `View (${count}) Projects`;

type ViewProjectProps = {
  documentTypeCode: Document['document_type_code'];
  documentId: Document['id'];
  projectSlugs?: Document['project_slugs'] | null;
};

const ViewProjectButton = ({
  documentTypeCode,
  documentId,
  projectSlugs,
}: ViewProjectProps) => {
  const [isModalOpen, setModalOpen] = useState<boolean>();

  const hasRelatedProjects = useMemo(() => {
    return projectSlugs && projectSlugs.length > 0;
  }, [projectSlugs]);

  const projectButtonLabel = useMemo(() => {
    if (projectSlugs?.length === 1) {
      return VIEW_PROJECT_SINGLE_BTN_LABEL;
    }

    return VIEW_PROJECT_MULTI_BTN_LABEL(projectSlugs?.length);
  }, [projectSlugs]);

  const onModalClose = useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);

  const handleOpenDocumentInNewTab = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (hasRelatedProjects) {
        return;
      }

      const documentURL = ddpUrl(documentId);

      trackProjectListClick({
        projectId: '',
        documentId,
        clickText: VIEW_DOCUMENT_BTN_LABEL,
        groupType: ProjectGroupTypes.UNCATEGORIZED,
        groupId: String(0),
      });
      window.open(`${documentURL}`, '_blank');
    },
    [documentTypeCode, documentId, projectSlugs, setModalOpen],
  );

  const handleOpenProjectInNewTab = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (!projectSlugs) {
        return;
      }

      if (projectSlugs?.length > 1) {
        setModalOpen(true);

        return;
      }

      // Only one project for this document.
      // Go to the PDP page without opening the document modal
      const projectURL = pdpUrl({
        projectSlug: projectSlugs[0],
      });

      trackProjectListClick({
        projectId: projectSlugs[0],
        clickText: projectButtonLabel,
        groupType: ProjectGroupTypes.UNCATEGORIZED,
        groupId: String(0),
      });

      window.open(`${projectURL}`, '_blank');
    },
    [documentTypeCode, documentId, projectSlugs, setModalOpen, projectButtonLabel],
  );

  if (
    !hasRelatedProjects &&
    documentTypeCode.toLowerCase() === DOCUMENT_TYPES.PLAT.toLowerCase()
  ) {
    /* Show nothing if its a PLAT with no projects */
    return (
      <FlexRow sx={emptyFieldStyle}>
        <EmptyField />
      </FlexRow>
    );
  }

  if (!hasRelatedProjects) {
    /* Render document button */
    return (
      <FlexRow style={containerStyle}>
        <Button variant="primary" onClick={handleOpenDocumentInNewTab}>
          {VIEW_DOCUMENT_BTN_LABEL}
        </Button>
      </FlexRow>
    );
  }

  /* Render project button */
  return (
    <FlexRow style={containerStyle}>
      <Button variant="primary" onClick={handleOpenProjectInNewTab}>
        {projectButtonLabel}
      </Button>
      {projectSlugs && projectSlugs?.length > 1 && (
        <MultipleProjectLinksModal
          projectSlugs={projectSlugs}
          documentTypeCode={documentTypeCode}
          documentId={documentId}
          isOpen={isModalOpen || false}
          onClose={onModalClose}
        />
      )}
    </FlexRow>
  );
};

export default ViewProjectButton;
